import React, { FC } from "react";
import voucherIcon from "components/Icon/icons/credit-card.svg";
import overpayIcon from "components/Icon/icons/overpay.svg";
import payPalIcon from "components/Icon/icons/paypal.svg";
import tetherUSDTLogo from "components/UsdtDepositForm/assets/TetherUSDTLogo.png";
import {
  PaymentFundsCol,
  PaymentFundsRow,
  PaymentImgIcon,
  StripeWordmark,
} from "containers/Books/styles/BooksAddFunds.style";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { updateAmountFunds } from "reduxFolder/reducers/books";
import { openRightPanel } from "reduxFolder/reducers/rightPanel";
import { amountFunds } from "reduxFolder/selectors/booksSelectors";
import { authSelectUser } from "reduxFolder/selectors/globalSelectors";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import { USDT_PAYMENT_LABEL } from "utils/paymentMethods";
import { FeatureGate } from "../../../../branding/FeatureGate";
import { useBooksPaymentMethods } from "../../../../branding/usePaymentMethods";
import { useBooksPayPalAvailability } from "../../../../branding/usePayPalAvailability";
import { useUsdtDepositsEnabled } from "../../../../branding/useUsdtDeposits";
import { panelSelectType } from "../../../../reduxFolder/selectors/rightPanelSelectors";
import visaLogo from "./../../images/visaLogo.png";

interface BooksAddFundsBlockProps {
  isPanelOpened: boolean;
  isBlockPanel?: boolean;
  openRightPanel: OpenRightPanel;
  updateAmountFunds: (amount: string) => void;
  panelType: string;
  user: any;
}

const BooksFundsPanelType = {
  CARD_FUNDS: "BOOKS_ADD_FUNDS",
  PAYPAL_FUNDS: "BOOKS_ADD_PAYPAL_FUNDS",
  OVERPAY_FUNDS: "BOOKS_ADD_OVERPAY_FUNDS",
  STRIPE_FUNDS: "BOOKS_ADD_STRIPE_FUNDS",
  VOUCHER_FUNDS: "BOOKS_VOUCHER_FUNDS",
  USDT_DEPOSIT: "BOOKS_USDT_DEPOSIT",
};

export const BooksAddFundsBlock: FC<BooksAddFundsBlockProps> = React.memo(
  ({ openRightPanel, panelType, user }) => {
    const { t } = useTranslation("common");
    const { isAvailable } = useBooksPaymentMethods(user);
    const { isAvailable: isPayPalAvailable } = useBooksPayPalAvailability(user);
    const usdtDepositsEnabled = useUsdtDepositsEnabled();

    const showBankCardForm = () => {
      openRightPanel(BooksFundsPanelType.CARD_FUNDS, Map({}));
    };

    const showPayPalForm = () => {
      openRightPanel(BooksFundsPanelType.PAYPAL_FUNDS, Map({}));
    };

    const showOverpayForm = () => {
      openRightPanel(BooksFundsPanelType.OVERPAY_FUNDS, Map({}));
    };

    const showStripeForm = () => {
      openRightPanel(BooksFundsPanelType.STRIPE_FUNDS, Map({}));
    };

    const showUsdtForm = () => {
      openRightPanel(BooksFundsPanelType.USDT_DEPOSIT, Map({}));
    };

    const showVoucherForm = () => {
      openRightPanel(BooksFundsPanelType.VOUCHER_FUNDS, Map({}));
    };

    return (
      <>
        {isAvailable("credit_card") && (
          <PaymentFundsRow
            onClick={showBankCardForm}
            selected={panelType === BooksFundsPanelType.CARD_FUNDS}
          >
            <PaymentFundsCol>
              <PaymentImgIcon>
                <img src={visaLogo} alt="Visa" style={{ height: "20px" }} />
              </PaymentImgIcon>
              {t("books.bankCard.title")}
            </PaymentFundsCol>
          </PaymentFundsRow>
        )}
        {isPayPalAvailable && (
          <PaymentFundsRow
            onClick={showPayPalForm}
            selected={panelType === BooksFundsPanelType.PAYPAL_FUNDS}
          >
            <PaymentFundsCol>
              <PaymentImgIcon>
                <img src={payPalIcon} alt="PayPal" style={{ height: "24px" }} />
              </PaymentImgIcon>
              PayPal
            </PaymentFundsCol>
          </PaymentFundsRow>
        )}
        {isAvailable("stripe") && (
          <PaymentFundsRow
            onClick={showStripeForm}
            selected={panelType === BooksFundsPanelType.STRIPE_FUNDS}
          >
            <PaymentFundsCol>
              <PaymentImgIcon>
                <StripeWordmark>Stripe</StripeWordmark>
              </PaymentImgIcon>
              {t("books.stripe.title")}
            </PaymentFundsCol>
          </PaymentFundsRow>
        )}
        <FeatureGate
          payment="overpay"
          overpayAllowedForUser={user.get("overpay_allowed", false)}
        >
          <PaymentFundsRow
            onClick={showOverpayForm}
            selected={panelType === BooksFundsPanelType.OVERPAY_FUNDS}
          >
            <PaymentFundsCol>
              <PaymentImgIcon>
                <img
                  src={overpayIcon}
                  alt="Overpay"
                  style={{ height: "24px" }}
                />
              </PaymentImgIcon>
              {t("books.overpay.title")}
            </PaymentFundsCol>
          </PaymentFundsRow>
        </FeatureGate>
        {usdtDepositsEnabled && (
          <PaymentFundsRow
            onClick={showUsdtForm}
            selected={panelType === BooksFundsPanelType.USDT_DEPOSIT}
          >
            <PaymentFundsCol>
              <PaymentImgIcon>
                <img
                  src={tetherUSDTLogo}
                  alt={USDT_PAYMENT_LABEL}
                  style={{ height: "24px" }}
                />
              </PaymentImgIcon>
              {USDT_PAYMENT_LABEL}
            </PaymentFundsCol>
          </PaymentFundsRow>
        )}
        <PaymentFundsRow
          onClick={showVoucherForm}
          selected={panelType === BooksFundsPanelType.VOUCHER_FUNDS}
        >
          <PaymentFundsCol>
            <PaymentImgIcon>
              <img src={voucherIcon} alt="Voucher" style={{ height: "24px" }} />
            </PaymentImgIcon>
            {t("books.voucher.title")}
          </PaymentFundsCol>
        </PaymentFundsRow>
      </>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    amountFunds,
    user: authSelectUser,
    panelType: panelSelectType,
  }),
  {
    updateAmountFunds,
    openRightPanel,
  },
);

export default withConnect(BooksAddFundsBlock);
